/**
 * What an organisation *is*.
 *
 * **The noun that changed.** An organisation does not *have a kind*; it *has modules* — letting,
 * developing, or both — and what it can open is exactly what it holds.
 *
 * **Neither module is privileged.** A client may buy letting alone, developing alone, or both, and
 * each has to work completely without the other: its own rail, its own home screen, its own
 * dashboard, its own arrears queue. Letting was the default-on module in the first draft of this
 * file — a hangover from the product having been a letting agency before it had modules — and that
 * is exactly wrong for a developer who never lets anything. ADR-0080.
 *
 * **`orgs.modules` is the purchase.** Not a preference, and not something a plan grants on top of
 * a floor: it is the record of what the customer bought, chosen at sign-up and changed by us. The
 * plan decides size and features — units, staff, message quota, the mobile app — and says nothing
 * about which business the organisation is in, because those are two independent questions. A
 * two-person developer belongs on Starter as readily as a two-person letting agency does.
 *
 * The one rule: **at least one.** An organisation with no module is an organisation with no
 * application, and a rail with nothing in it is a bug that looks like a blank screen.
 *
 * Nothing outside this file compares a module code, for the same reason nothing outside `plans.ts`
 * compares a plan code.
 */

export const MODULES = ["letting", "developing"] as const;
export type ModuleCode = (typeof MODULES)[number];

export interface ModuleInfo {
  code: ModuleCode;
  /** What the switcher says. */
  label: string;
  /** What an organisation running only this module calls itself. */
  noun: string;
  blurb: string;
  /** Where this module's rail lands. Also where somebody who has only it is sent on sign-in. */
  home: string;
  /** For the sign-up question, where somebody is choosing what they do rather than reading a rail. */
  question: string;
}

export const MODULE_INFO: Record<ModuleCode, ModuleInfo> = {
  letting: {
    code: "letting",
    label: "Letting",
    noun: "agency",
    blurb: "Properties let to tenants: rent, renewals, arrears and reminders.",
    home: "/app",
    question: "I let property to tenants",
  },
  developing: {
    code: "developing",
    label: "Development",
    noun: "developer",
    blurb: "Properties sold to buyers: plots and units, payment plans, allocations and documents.",
    home: "/app/development",
    question: "I sell plots and houses to buyers",
  },
};

/**
 * The module an organisation falls back to when it holds none we recognise.
 *
 * **This is a floor, not a default.** It exists for exactly two cases: a row written before this
 * column did (`modules` is null, and every one of those organisations was a letting agency), and a
 * row whose stored value is nonsense. Neither is "we assume everybody lets property" — a developer
 * who never lets anything must be able to hold `["developing"]` and see no letting anywhere, and
 * `normaliseModules` is what guarantees it.
 */
export const FALLBACK_MODULE: ModuleCode = "letting";

/**
 * What the two modules can share, named once so a screen and a query cannot disagree about it.
 *
 * Everything *not* on this list belongs to one module and is separated already — a letting
 * portfolio, its units, tenancies and invoices; a development's properties, plots and purchases.
 * These six are the ones that could sensibly be either, and therefore the only ones a setting is
 * needed for. ADR-0082.
 */
export const SHARED_ACROSS_MODULES = [
  { key: "clients", label: "Clients", blurb: "Tenants, landlords and buyers in one contact list." },
  { key: "payments", label: "Payments", blurb: "One receipt series and one record of money received." },
  { key: "expenses", label: "Expenses", blurb: "Costs recorded against either business." },
  { key: "bankAccounts", label: "Bank accounts", blurb: "The accounts money is collected into and paid out from." },
  { key: "documents", label: "Documents", blurb: "Files held against a client or a property." },
  { key: "reports", label: "Reports", blurb: "Money reports built on the records above." },
] as const;

export function isModule(value: unknown): value is ModuleCode {
  return typeof value === "string" && (MODULES as readonly string[]).includes(value);
}

/**
 * What an organisation actually holds, from whatever is stored.
 *
 * Deduped, and in declared order rather than insertion order, so a switcher does not shuffle
 * between requests. Anything unrecognised is dropped rather than trusted.
 *
 * An empty result becomes `[FALLBACK_MODULE]` — see the note there. That is the only circumstance
 * in which letting appears without having been asked for.
 */
export function normaliseModules(stored?: string[] | null): ModuleCode[] {
  const held = new Set<ModuleCode>();
  for (const code of stored ?? []) if (isModule(code)) held.add(code);
  const ordered = MODULES.filter((code) => held.has(code));
  return ordered.length ? ordered : [FALLBACK_MODULE];
}

/** Kept as the name every caller already uses. Takes what is stored, and nothing else. */
export const modulesFor = normaliseModules;

export function hasModule(stored: string[] | null | undefined, code: ModuleCode): boolean {
  return normaliseModules(stored).includes(code);
}

/**
 * Where somebody with these modules should land.
 *
 * The first module they hold, in declared order — so a developer who never lets anything opens on
 * their own properties rather than on an empty letting dashboard, which is what the product used
 * to do and is the whole complaint this file exists to answer.
 */
export function homeFor(stored: string[] | null | undefined): string {
  return MODULE_INFO[normaliseModules(stored)[0]].home;
}

/**
 * Which module a path belongs to — or neither.
 *
 * **Some screens belong to both.** Clients, payments, reports, bank accounts, settings and support
 * are shared, and answering "letting" for them was a real bug: somebody working in the development
 * rail clicked Payments and the whole navigation flipped underneath them. So this returns `null`
 * for a shared path and the caller decides, from `?module=` in the address.
 *
 * Deliberately a prefix test on two short lists rather than a route table: it decides what the
 * navigation looks like, not what may be opened, and the API decides that.
 *
 * **One prefix owns the whole development module, and this is why the module moved under it.** A
 * developer calls the scheme they are selling a property, and a letting agency calls a building
 * one — the same word, two different records, and this function has nothing but the address to
 * tell them apart. `/app/properties` was already letting's, so development's own list went to
 * `/app/development/properties` rather than fighting it for the word.
 *
 * The five entries under `/app/development` are the addresses the module used to have. They are
 * kept because the routes that redirect them still draw a rail on the way through, and a rail that
 * flickers to the wrong module mid-redirect is the thing the redirect exists to avoid.
 */
const DEVELOPING_PREFIXES = [
  "/app/development",
  "/app/estates",
  "/app/plots",
  "/app/purchases",
  "/app/allocations",
  "/app/buyers",
];

const LETTING_PREFIXES = [
  "/app/properties",
  "/app/units",
  "/app/tenancies",
  "/app/leases",
  /* Three that were missing, and the API had already decided all three: `/agreements`,
     `/inspections` and `/payouts` sit in `LETTING_ONLY` in `apps/api/src/index.ts`, behind
     `requireModule("letting")`. Left out of this list they counted as **shared**, and the two
     halves of the product then disagreed about the same address in both directions — a
     development-only agency typing `/app/payouts` was drawn the letting screen and met a 402 from
     the endpoint behind it, instead of the door that says which module is missing; and a reader
     holding both modules who reached one of them from the development rail kept the development
     rail on a screen that is letting's alone. A tenancy agreement, a property inspection and a
     landlord payout are letting records; there is no development counterpart to any of them. */
  "/app/agreements",
  "/app/inspections",
  "/app/payouts",
  "/app/invoices",
  /* `/app/expenses` was here and is now **shared**, which is why it is a comment rather than a
     deletion: a cost can be spent on a scheme as readily as on a building, and the screen answers
     for whichever module is in force. Left in this list it counted as letting-owned, so
     `?module=developing` was ignored — the rail stayed blue while the page showed the letting
     books, which is the disagreement `moduleForPath` exists to prevent. ADR-0161. */
  "/app/maintenance",
  "/app/reminders",
  "/app/templates",
  "/app/enquiries",
  "/app/listings",
  "/app/settlements",
];

const under = (path: string, prefixes: string[]) =>
  prefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));

export function moduleForPath(path: string): ModuleCode | null {
  if (under(path, DEVELOPING_PREFIXES)) return "developing";
  if (under(path, LETTING_PREFIXES)) return "letting";
  // `/app` itself, and everything shared. Neither owns it.
  return null;
}

/** True when this screen belongs to both modules and the address has to say which is meant. */
export function isSharedPath(path: string): boolean {
  return moduleForPath(path) === null && path !== "/app";
}

/**
 * Which module's rail to draw.
 *
 * Three sources, in order, and the order is the whole of it:
 *
 * 1. **What the path owns.** A link into a development property brings the developer rail with it.
 * 2. **What the address says**, for a screen both modules share — `?module=developing`. Without
 *    this, pressing Payments from the development rail dropped somebody into letting, which is the
 *    bug this parameter exists to fix.
 * 3. **The first module held**, for `/app` and for anything that says nothing.
 *
 * Narrowed at every step to what the organisation actually holds, so a letting-only agency that
 * types a development address still gets its own rail rather than one it cannot use an item of.
 */
export function railFor(path: string, stored: string[] | null | undefined, search?: string): ModuleCode {
  const held = normaliseModules(stored);
  const owned = moduleForPath(path);
  if (owned && held.includes(owned)) return owned;

  if (search) {
    const asked = new URLSearchParams(search).get("module");
    if (isModule(asked) && held.includes(asked)) return asked;
  }

  return held[0];
}

/**
 * Where a module's tab in the switcher points.
 *
 * **The bug this exists to fix.** The switcher linked at `MODULE_INFO[code].home` directly, and
 * letting's home is `/app` — which `moduleForPath` deliberately says nobody owns, because the
 * dashboard is drawn for whichever module you are in. So pressing "Letting" from the development
 * rail navigated to a path that said nothing, `useActiveModule` fell through to what was
 * remembered, and the answer came back "developing". The address changed and the rail did not: a
 * switch that visibly does nothing.
 *
 * The switcher is the one control whose entire purpose is to state which module is meant, so it
 * states it, rather than relying on the path to imply it. `withModule` cannot do this job — it
 * leaves `/app` alone on purpose, because every other link to the dashboard *should* follow the
 * reader rather than steer them.
 *
 * A module whose home it already owns is left clean: `/app/development` needs no marker, and an
 * address with a redundant parameter in it is one somebody will copy into a bug report.
 */
export function moduleHome(code: ModuleCode): string {
  const home = MODULE_INFO[code].home;
  return moduleForPath(home) === code ? home : `${home}?module=${code}`;
}

/**
 * A link to a shared screen, kept inside the module the reader is in.
 *
 * Only where it is needed: a path one module owns already says so, and an organisation with a
 * single module has nothing to be confused about.
 */
export function withModule(to: string, module: ModuleCode, held: ModuleCode[]): string {
  const [path] = to.split("?");
  if (held.length < 2 || !isSharedPath(path)) return to;
  const [, query = ""] = to.split("?");
  const params = new URLSearchParams(query);
  params.set("module", module);
  return `${path}?${params.toString()}`;
}
